import { useEffect, useState } from "react";
import { api, fmtError } from "../../api";

const ROLES = [
  { v: "master", l: "👑 Master" },
  { v: "client_admin", l: "🏢 Client Admin" },
  { v: "planner", l: "📋 Planner" },
  { v: "view_only", l: "👁️ View Only" },
];

export default function Users() {
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [stores, setStores] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => {
    api.get("/users").then(r => setUsers(r.data)).catch(() => {});
    api.get("/clients").then(r => setClients(r.data)).catch(() => {});
    api.get("/stores").then(r => setStores(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const deactivate = async (u) => {
    if (!window.confirm(`Deactivate ${u.email}?`)) return;
    await api.delete(`/users/${u.id}`);
    load();
  };

  return (
    <div data-testid="users-page">
      <div className="topbar">
        <div>
          <div className="page-title">Users <span className="dim">& Access</span></div>
          <div className="page-meta"><span>Master only · {users.length} users total</span></div>
        </div>
        <div className="topbar-right">
          <button className="btn btn-primary" onClick={() => { setEditing(null); setShowModal(true); }} data-testid="add-user-btn">+ Add User</button>
        </div>
      </div>

      <div className="card">
        <div className="card-header"><div><div className="card-title">All Users</div><div className="card-sub">Manage access & scope</div></div></div>
        <table>
          <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Scope</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`}>
                <td><div style={{ fontWeight: 600 }}>{u.name}</div></td>
                <td style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 12 }}>{u.email}</td>
                <td><span className="badge b-role">{ROLES.find(r => r.v === u.role)?.l || u.role}</span></td>
                <td style={{ fontSize: 11, color: "var(--muted)", fontFamily: "'JetBrains Mono',monospace" }}>
                  {u.role === "master" ? "All clients / all stores"
                    : u.role === "client_admin" ? `${(u.client_ids || []).length} clients`
                    : `${(u.store_ids || []).length} stores`}
                </td>
                <td><span className={`badge ${u.active ? "b-active" : "b-inactive"}`}>● {u.active ? "Active" : "Inactive"}</span></td>
                <td>
                  <div style={{ display: "flex", gap: 5 }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => { setEditing(u); setShowModal(true); }} data-testid={`edit-user-${u.id}`}>Edit</button>
                    {u.active && <button className="btn btn-danger btn-sm" onClick={() => deactivate(u)} data-testid={`deactivate-user-${u.id}`}>Deactivate</button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <UserModal
          user={editing}
          clients={clients}
          stores={stores}
          onClose={() => setShowModal(false)}
          onSaved={() => { setShowModal(false); load(); }}
        />
      )}
    </div>
  );
}

function UserModal({ user, clients, stores, onClose, onSaved }) {
  const isEdit = !!user;
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    role: user?.role || "planner",
    client_ids: user?.client_ids || [],
    store_ids: user?.store_ids || [],
    password: "",
    pin: "",
    active: user?.active ?? true,
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const upd = (k, v) => setForm({ ...form, [k]: v });
  const toggle = (arr, id) => arr.includes(id) ? arr.filter(x => x !== id) : [...arr, id];

  const save = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      if (isEdit) {
        const patch = { name: form.name, role: form.role, client_ids: form.client_ids, store_ids: form.store_ids, active: form.active };
        if (form.password) patch.password = form.password;
        await api.patch(`/users/${user.id}`, patch);
        if (form.pin && form.pin.length === 4) {
          await api.post(`/users/${user.id}/reset-pin`, { pin: form.pin });
        }
      } else {
        if (!form.password || form.pin.length !== 4) {
          setErr("Password and 4-digit PIN required"); setBusy(false); return;
        }
        await api.post("/users", form);
      }
      onSaved();
    } catch (e) { setErr(fmtError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="modal-overlay" onClick={(e) => e.target.className === "modal-overlay" && onClose()}>
      <form className="modal" onSubmit={save}>
        <div className="modal-header">
          <div><div className="modal-title">{isEdit ? "Edit User" : "Add User"}</div><div className="modal-sub">Assign role and scope</div></div>
          <div className="modal-close" onClick={onClose}>✕</div>
        </div>
        <div className="modal-body">
          {err && <div className="alert alert-err">{err}</div>}
          <div className="form-row form-group">
            <div><label>Name</label><input required value={form.name} onChange={(e) => upd("name", e.target.value)} data-testid="user-name-input" /></div>
            <div><label>Email</label><input required type="email" disabled={isEdit} value={form.email} onChange={(e) => upd("email", e.target.value)} data-testid="user-email-input" /></div>
          </div>
          <div className="form-group">
            <label>Role</label>
            <select value={form.role} onChange={(e) => upd("role", e.target.value)} data-testid="user-role-select">
              {ROLES.map(r => <option key={r.v} value={r.v}>{r.l}</option>)}
            </select>
          </div>
          {form.role !== "master" && (
            <>
              <div className="form-group">
                <label>Assigned Clients</label>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                  {clients.map(c => (
                    <label key={c.id} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", padding: "8px 12px", background: "var(--s2)", borderRadius: 8, border: "1px solid var(--b1)", textTransform: "none", fontFamily: "'Instrument Sans',sans-serif", fontSize: 13, letterSpacing: 0 }}>
                      <input type="checkbox" style={{ width: "auto" }} checked={form.client_ids.includes(c.id)} onChange={() => upd("client_ids", toggle(form.client_ids, c.id))} />
                      {c.avatar} {c.name}
                    </label>
                  ))}
                </div>
              </div>
              {["planner", "view_only"].includes(form.role) && (
                <div className="form-group">
                  <label>Assigned Stores</label>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, maxHeight: 200, overflowY: "auto" }}>
                    {stores.map(s => (
                      <label key={s.id} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", padding: "8px 12px", background: "var(--s2)", borderRadius: 8, border: "1px solid var(--b1)", textTransform: "none", fontFamily: "'Instrument Sans',sans-serif", fontSize: 13, letterSpacing: 0 }}>
                        <input type="checkbox" style={{ width: "auto" }} checked={form.store_ids.includes(s.id)} onChange={() => upd("store_ids", toggle(form.store_ids, s.id))} />
                        {s.name} <span style={{ color: "var(--muted)", fontSize: 10 }}>({s.platform}/{s.country})</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
          <div className="form-row form-group">
            <div><label>{isEdit ? "New Password (optional)" : "Temp Password"}</label><input type="password" value={form.password} onChange={(e) => upd("password", e.target.value)} data-testid="user-password-input" /></div>
            <div><label>{isEdit ? "Reset PIN (optional)" : "4-digit PIN"}</label><input maxLength={4} pattern="\d{4}" value={form.pin} onChange={(e) => upd("pin", e.target.value.replace(/\D/g,""))} data-testid="user-pin-input" /></div>
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={busy} data-testid="save-user-btn">{busy ? "Saving..." : "Save"}</button>
        </div>
      </form>
    </div>
  );
}
